import React, { useEffect, useState, useContext, useLayoutEffect } from 'react';
import {
  AppHeader,
  Button,
  ButtonGroup,
  Icon,
  IconButton,
  CircularProgress,
  Typography,
  Stack,
  Box,
  Container,
} from '@ace-clublabs/shared-component-library';
import { useHistory } from 'react-router-dom';
import { Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { getNextPage } from '../utils/getNextPage';
import { ProductType } from '../application/models/NewJoinSessionData';
import { RemoveWarningModal, RemoveWarningModalContentProps } from '../components/RemoveWarningModal';
import { TooltipModal, TooltipModalContentProps, TooltipModalSubContent } from '../components/TooltipModal';
import {
  getAssociatePrices,
  getAssociateOriginalPrice,
  getAssociateDiscountedPrice,
  historyPushState,
  getProgressProps,
} from '../utils/helpers';
import { validateAssociates } from '../utils/validations';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { TotalCostHeader } from '../components/TotalCostHeader';
import { AddOn } from '../components/AddOn';
import { UtagType } from '../utils/UtagHelper';
import { sources } from '../api/sources';
import { hashStringWithSHA256 } from '../utils/hash';
import { ERSAddOn } from '../components/ERSAddOn';
import { RVAddOn } from '../components/RVAddOn';
import { AdditionalMemberTooltip } from '../components/AdditionalMemberTooltip';
import { BenefitsPage } from './BenefitsPage';
import { calculateFamilyUpgradeCost } from '../utils/helpers';
import { numberHelper } from '../utils/numberHelper';
import { CurrentPayPlan, FlowType } from '@ace-clublabs/common-membership-api-types';
import { getRecurringPeriodLabel } from '../utils/stringHelper';
import { AppProgressHeader } from '../components/AppProgressHeader';

export const BuildMembershipPage: React.FC = () => {
  const {
    membershipInfo,
    sessionData,
    updateAssociates,
    refreshSession,
    loadingInfo,
    isPriceCartLoading,
    sendTag,
    getNewBusinessCost,
  } = useContext(MembershipAppStateContext);
  const history = useHistory();
  const [selectedRV, setSelectRV] = useState(sessionData.formData.products?.includes(ProductType.RV) ? true : false);
  const [selectedERS, setSelectedERS] = useState(
    sessionData.formData.products?.includes(ProductType.ERS) ? true : false
  );
  const [isTagging, setIsTagging] = useState(true);
  const [deleteInfo, setDeleteInfo] = useState<RemoveWarningModalContentProps | null>(null);
  const [tooltipInfo, setTooltipInfo] = useState<TooltipModalContentProps | null>(null);
  const [tooltipContent, setTooltipContent] = useState('');
  const [memAddOnTooltipInfo, setMemAddOnTooltipInfo] = useState<TooltipModalContentProps | null>(null);

  const currentUrl = window.location.href;
  const urlSearchParams = new URL(currentUrl).searchParams;

  const isChangingMembershipLevelParam = urlSearchParams.get('isChangingMembershipLevel') === 'true';
  const [isChangingMembershipLevel, setIsChangingMembershipLevel] = useState(isChangingMembershipLevelParam);

  useEffect(() => {
    setIsChangingMembershipLevel(isChangingMembershipLevelParam);
  }, [isChangingMembershipLevelParam]);

  const associates = sessionData?.formData.associates ?? [];

  const showBenefitPage = () => {
    urlSearchParams.set('isChangingMembershipLevel', 'true');
    const newUrl = currentUrl.split('?')[0] + '?' + urlSearchParams.toString();
    historyPushState({ path: newUrl }, '', newUrl);
    setIsChangingMembershipLevel(true);
  };

  const closeBenefitsPage = () => {
    window.history.back();
  };

  const { hasReachedMaxAssociates } = validateAssociates(membershipInfo!, sessionData.formData);

  const isGiftFlow = sessionData.flowType === FlowType.GIFT;

  const progressProps = getProgressProps(sessionData.flowType!, Pages.BuildMembershipPage);

  const familyUpgradeFee = calculateFamilyUpgradeCost(membershipInfo!, sessionData);

  const tag = async () => {
    const { firstName, lastName, gender, residenceAddress, email, mobileNumber } = sessionData.formData;
    const { streetAddress, state, city, zipCode } = residenceAddress;

    const encryptedTags = await sources.encryptAnalytics({
      ed_olm_1: firstName,
      ed_olm_2: lastName,
      ed_olm_3: gender,
      ed_olm_4: streetAddress,
      ed_olm_5: city,
      ed_olm_6: state,
      ed_olm_7: zipCode,
      ed_olm_8: email,
      ed_olm_9: mobileNumber,
    });

    const overrides =
      sessionData.flowType !== FlowType.GIFT
        ? {
            ...encryptedTags,
            hd_olm_1: hashStringWithSHA256(email!),
            hd_olm_2: hashStringWithSHA256(mobileNumber!),
          }
        : {};
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:add ons' : 'add ons';

    sendTag(UtagType.view, tagPage, overrides);
    setIsTagging(false);
  };

  useEffect(() => {
    if (!sessionData) {
      return;
    }
    setSelectRV(sessionData.formData.products?.includes(ProductType.RV) ? true : false);
    setSelectedERS(sessionData.formData.products?.includes(ProductType.ERS) ? true : false);
  }, [sessionData.formData.products]);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo) {
      return;
    }

    tag();
  }, [membershipInfo]);

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null || isTagging;
  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </Container>
    );
  }

  return isChangingMembershipLevel ? (
    <BenefitsPage onContinue={closeBenefitsPage} isUpgrading={true} />
  ) : (
    <div className="build-membership-view">
      <AppProgressHeader {...progressProps} />

      <TotalCostHeader
        recurringPeriodLabel={getRecurringPeriodLabel(sessionData)}
        onChangeClick={showBenefitPage}
        showBenefitBullets={true}
      />
      <div className="primary-member-container">
        <Stack direction="column" className="primary-member-info">
          <Typography className="primary-member-title" variant="b1Medium">
            Primary member
          </Typography>
          <Typography
            variant="b1Regular"
            className="primary-member-name qm-mask"
            data-di-mask="true"
            sx={{ marginTop: '6px' }}
          >{`${sessionData.formData.firstName} ${sessionData.formData.lastName}`}</Typography>
        </Stack>
      </div>
      <div className="membership-add-ons-container">
        <div className="benefit-add-ons">
          <Typography className="benefit-add-ons-title" variant="b1Medium">
            Additional members
          </Typography>
          <IconButton
            aria-label="membership addon info"
            className="add-member-info-icon"
            onClick={() => {
              setMemAddOnTooltipInfo({
                tooltipTitle: 'Add a member',
              });
            }}
          >
            <Icon iconName="circle-info-regular" sx={{ color: '#707070' }} />
          </IconButton>
        </div>

        {familyUpgradeFee > 0 ? (
          <Box marginBottom={'0.5rem'}>
            <Typography className="family-upgrade-warning" variant="b3Regular">
              There is a {numberHelper.formatMoney(familyUpgradeFee)} fee for a family upgrade
            </Typography>
          </Box>
        ) : (
          <></>
        )}

        {familyUpgradeFee && associates.length > 0 ? (
          <AddOn
            text="Family upgrade fee"
            price={`$${familyUpgradeFee}/${
              sessionData.formData.billPlan == CurrentPayPlan.RECURRINGMONTHLY ? 'mo' : 'yr'
            }`}
            isSelected={true}
            icon=""
            type="family-upgrade"
            hideAddDeleteIcon={true}
          />
        ) : (
          <></>
        )}

        {associates.map((associate, i) => (
          <AddOn
            onAdd={() => {}}
            onEdit={() => {
              history.push({
                pathname: Pages.AddAssociatePage,
                state: { associate: associate, isEditing: true },
              });
            }}
            onDelete={() => {
              setDeleteInfo({
                title: `Are you sure you want to remove ${associate.firstName} ${associate.lastName} from your membership?`,
                confirmRemove: () => {
                  updateAssociates('remove', associate);
                },
              });
            }}
            text={`${associate.firstName} ${associate.lastName}`}
            price={getAssociateOriginalPrice(sessionData, associate)}
            discountedPrice={getAssociateDiscountedPrice(sessionData, associate)}
            isSelected={true}
            icon=""
            type="associate"
          />
        ))}

        {hasReachedMaxAssociates ? (
          <></>
        ) : (
          <AddOn
            onAdd={() => {
              history.push(Pages.AddAssociatePage);
            }}
            text="Add a family member"
            price={getAssociatePrices(membershipInfo, sessionData)}
            isSelected={false}
            icon="user-plus-regular"
            type={'associate'}
          />
        )}
        <RVAddOn
          setTooltipInfo={setTooltipInfo}
          setTooltipContent={setTooltipContent}
          setDeleteInfo={setDeleteInfo}
          selectedRV={selectedRV}
          onUpgradeClick={showBenefitPage}
        />

        {isGiftFlow ? (
          <></>
        ) : (
          <ERSAddOn
            setTooltipInfo={setTooltipInfo}
            setTooltipContent={setTooltipContent}
            setDeleteInfo={setDeleteInfo}
            selectedERS={selectedERS}
          />
        )}

        <div className="review-page-btn">
          <ButtonGroup>
            <Button
              id="nextButton"
              onClick={() => {
                history.push(getNextPage(Pages.BuildMembershipPage, sessionData.flowType));
              }}
              type="submit"
              className="btn-large"
              label={
                <>
                  {isPriceCartLoading ? (
                    <div className="button-spinner">
                      <CircularProgress variant="indeterminate" size={20} color="inherit" />{' '}
                    </div>
                  ) : (
                    <></>
                  )}
                  Next
                </>
              }
              disabled={isPriceCartLoading}
            />
          </ButtonGroup>
        </div>
      </div>
      <RemoveWarningModal
        title={deleteInfo?.title ?? ''}
        warningContent={deleteInfo?.warningContent ?? ''}
        showModal={!!deleteInfo}
        closeModal={() => setDeleteInfo(null)}
        confirmRemove={() => {
          deleteInfo?.confirmRemove();
        }}
      />
      <AdditionalMemberTooltip
        memAddOnTooltipInfo={memAddOnTooltipInfo}
        setMemAddOnTooltipInfo={setMemAddOnTooltipInfo}
        roles={membershipInfo.roles}
      />
      <TooltipModal
        tooltipTitle={tooltipInfo?.tooltipTitle ?? ''}
        showModal={!!tooltipInfo}
        closeModal={() => {
          setTooltipInfo(null);
          setTooltipContent('');
        }}
      >
        <TooltipModalSubContent tooltipContent={tooltipContent}></TooltipModalSubContent>
      </TooltipModal>
    </div>
  );
};
